import React from "react";

import { companyApplicationApi } from "EmployerApplication/api";
import { getCompanyApplication } from "EmployerApplication/Selectors";
import { api } from "EmployerApplication/urls";
import { getCompanyApplicationByCompanyProfileId } from "EntitiesApi/api";
import createDecorator from "final-form-focus";
import { Form } from "react-final-form";
import { useSelector, useDispatch } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import companyToken from "Utils/companyToken";
import fetchWithCSRFToken from "Utils/fetchWithCSRFToken";
import { defaultHandler } from "Utils/http";

import { EDITED_EMPLOYEE } from "../Actions";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "Cobalt/Modal";
import Button from "Common/Button";
import SubmissionErrorWrapper from "Common/forms/SubmissionErrorWrapper";
import Header from "Cobalt/Header";
import Icon from "Common/Icon";

import PartnerAddEmployeeForm from "./PartnerAddEmployeeForm";

const focusOnErrors = createDecorator();

const PartnerEditEmployeeModal = ({ employee, include1099, includeOwner, show, onHide }) => {
  const dispatch = useDispatch();
  const { companyProfileId } = useSelector(getCompanyApplication) || {};
  const onSubmit = async (values) => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "edit_employee_modal",
      label: "update",
    });

    try {
      const response = await fetchWithCSRFToken(api.updateEmployee, {
        method: "PUT",
        body: JSON.stringify({
          employee_hash_id: employee.hash_id,
          employee: values.employee,
          company_id_token: companyToken(),
        }),

        processData: false,
      });

      await defaultHandler(response);
      const json = await response.json();

      dispatch({ type: EDITED_EMPLOYEE, employee: json });
      dispatch(
        getCompanyApplicationByCompanyProfileId(companyProfileId, {
          replace: true,
        }),
      );

      // TODO: Editing an employee should invalidate the 'Application' tag. So we can remove this as well.
      dispatch(companyApplicationApi.endpoints.getCurrentCompanyApplication.initiate());

      onHide();
    } catch (e) {
      new SubmissionErrorWrapper(e)
        .forForbiddenError(() => {
          window.location.href = "/unexpected-error";
        })
        .orRethrow();
    }
  };
  const handleClose = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "edit_employee_modal",
      label: "cancel",
    });

    onHide();
  };

  const fallBackName = () => {
    if (include1099 && includeOwner) {
      return "this owner or contractor";
    }
    if (include1099 && !includeOwner) {
      return "this contractor";
    }
    if (!include1099 && includeOwner) {
      return "this owner";
    }
  };
  const person = employee ? employee.full_name.replace(/^\s+|\s+$/g, "") : fallBackName();

  const initialValues = {
    employee: {
      ...employee,
      employee_fill: employee.email ? "Yes" : "No",
    },
  };

  return (
    <Modal show={show} onHide={handleClose} className="EditEmployeeModal">
      <Form onSubmit={onSubmit} decorators={[focusOnErrors]} initialValues={initialValues}>
        {({ handleSubmit, invalid, submitting, values }) => {
          const { employee: { employee_fill } = {} } = values;

          return (
            <form onSubmit={handleSubmit}>
              <ModalHeader closeButton>
                <Header level={7}>Edit {person}</Header>
              </ModalHeader>
              <ModalBody>
                <PartnerAddEmployeeForm
                  employeeFullName={person}
                  employeeWillFillApplication={employee_fill}
                  canLinkAccount={false}
                  showOptionW2={false}
                  showOption1099={include1099}
                  showOptionOwner={includeOwner}
                />
              </ModalBody>
              <ModalFooter>
                <Button color="gray" onClick={handleClose} mr={3}>
                  Cancel
                </Button>
                <Button id="updateButton" type="submit" disabled={invalid || submitting}>
                  {submitting ? (
                    <>
                      <Icon icon="cog" spin mr={3} flavor="solid" />
                      Loading...
                    </>
                  ) : (
                    "Update"
                  )}
                </Button>
              </ModalFooter>
            </form>
          );
        }}
      </Form>
    </Modal>
  );
};

export default PartnerEditEmployeeModal;
